import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { Input } from '@ui/atoms';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import isSameDay from 'date-fns/isSameDay';
import endOfDay from 'date-fns/endOfDay';
import startOfDay from 'date-fns/startOfDay';
import formatDate from 'date-fns/format';
import fr from 'date-fns/locale/fr';
import useTranslate from '@hooks/useTranslate';

// --- petit hook pour fermer au clic extérieur (sans findDOMNode)
function useOnClickOutside(ref, onOutside) {
  useEffect(() => {
    function handler(ev) {
      const el = ref.current;
      if (el && !el.contains(ev.target)) onOutside(ev);
    }
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [ref, onOutside]);
}

function Datepicker({ value, onChange, minDate = undefined, maxDate = undefined, withTime = false }) {
  const { t } = useTranslate();

  // état d’ouverture du “popover” + valeur sélectionnée (contrôlée par prop `value`)
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(value);
  useEffect(() => {
    setSelected(value);
  }, [value]);

  const format = withTime ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy';

  // mêmes règles que ton code pour min/max heures
  const minTime = minDate && isSameDay(selected, minDate) ? minDate : startOfDay(new Date());
  const maxTime = maxDate && isSameDay(selected, maxDate) ? maxDate : endOfDay(new Date());

  const containerRef = useRef(null);
  useOnClickOutside(containerRef, () => setOpen(false));

  const handleSelectDay = (date) => {
    if (!date) return;
    if (withTime) {
      // conserve l’heure actuelle si déjà définie
      const h = selected?.getHours() ?? 0;
      const m = selected?.getMinutes() ?? 0;
      const next = new Date(date);
      next.setHours(h, m, 0, 0);
      setSelected(next);
      onChange(next);
      // on ne ferme pas encore: l’utilisateur peut ajuster l’heure
    } else {
      setSelected(date);
      onChange(date);
      setOpen(false);
    }
  };

  const handleTimeChange = (e) => {
    const [h, m] = e.target.value.split(':').map(Number);
    const base = selected ?? new Date();
    const next = new Date(base);
    next.setHours(h, m, 0, 0);
    setSelected(next);
    onChange(next);
  };

  // valeur affichée dans l’Input (comme avant)
  const inputValue = selected ? formatDate(selected, format) : '';

  return (
    <div style={{ position: 'relative' }} ref={containerRef}>
      <Input value={inputValue} readOnly onFocus={() => setOpen(true)} />

      {open && (
        <div
          style={{
            position: 'absolute',
            zIndex: 9999,
            background: 'white',
            border: '1px solid #ccc',
            borderRadius: 8,
            padding: '12px',
            boxShadow: '0 6px 24px rgba(0,0,0,0.12)',
          }}
        >
          <DayPicker
            mode="single"
            selected={selected}
            onSelect={handleSelectDay}
            fromDate={minDate}
            toDate={maxDate}
            locale={fr}
          />

          {withTime && (
            <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              <label>
                {t('common.hour')}
                {/* step 900s = 15min, équiv. à timeIntervals={15} */}
                <input
                  type="time"
                  step={900}
                  value={selected ? formatDate(selected, 'HH:mm') : '00:00'}
                  min={formatDate(minTime, 'HH:mm')}
                  max={formatDate(maxTime, 'HH:mm')}
                  onChange={handleTimeChange}
                  style={{ marginLeft: 8 }}
                />
              </label>
            </div>
          )}

          <div style={{ marginTop: 8, textAlign: 'right' }}>
            <button type="button" onClick={() => setOpen(false)}>
              {t('common.verb.cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

Datepicker.propTypes = {
  value: PropTypes.instanceOf(Date).isRequired,
  onChange: PropTypes.func.isRequired,
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
  withTime: PropTypes.bool,
};

export default Datepicker;
